import { allocateUintArray } from "./IntArray";

/** @import { SpatialTree, TreeNodes } from "./SpatialTreeInterface" */

/**
 * Node storage for an n-ary spatial tree (e.g. octree, merged multi-file BVH).
 * Children are stored in a flat Int32Array referenced by per-node (offset, count) pairs.
 *
 * Implements the {@link TreeNodes} interface (see SpatialTreeInterface.js).
 */
export class NaryBvhNodeArray {
  /**@type {number} Number of allocated nodes. Fixed on construction. */
  nodeCount;

  /**
   * @param {number} nodeCount  Number of nodes that will be allocated.
   */
  constructor(nodeCount) {
    this.nodeCount = nodeCount;

    this._boxes = new Float32Array(nodeCount * 6);
    this._childOffsets = new Int32Array(nodeCount);
    this._childCounts = new Uint16Array(nodeCount);
    this._primStarts = new Int32Array(nodeCount);
    this._primCounts = new Uint16Array(nodeCount);
    this._flags = new Uint16Array(nodeCount);
    this._largestPrimSizes = new Float32Array(nodeCount);

    this._children = new Int32Array(nodeCount - 2); // -2 for the opaque and transparent root nodes
    this._childrenUsed = 0;
  }

  // ----------------------------------------------------------------
  //  Builder API (used during construction only)
  // ----------------------------------------------------------------

  /**
   * Set the children of a node. Must be called at most once per node.
   * @param {number} nodeIdx
   * @param {number[]} childIndices
   */
  setChildren(nodeIdx, childIndices) {
    const count = childIndices.length;
    if (count === 0) return;
    this._childOffsets[nodeIdx] = this._childrenUsed;
    this._childCounts[nodeIdx] = count;
    for (let i = 0; i < count; i++) {
      this._children[this._childrenUsed++] = childIndices[i];
    }
  }

  /**
   * @param {number} nodeIdx
   * @param {THREE.Box3} src
   */
  setBoxThree(nodeIdx, src) {
    const off = nodeIdx * 6;
    this._boxes[off] = src.min.x;
    this._boxes[off + 1] = src.min.y;
    this._boxes[off + 2] = src.min.z;
    this._boxes[off + 3] = src.max.x;
    this._boxes[off + 4] = src.max.y;
    this._boxes[off + 5] = src.max.z;
  }

  /**
   * @param {number} nodeIdx
   * @param {number} start
   */
  setPrimStart(nodeIdx, start) {this._primStarts[nodeIdx] = start;}

  /**
   * @param {number} nodeIdx
   * @param {number} count
   */
  setPrimCount(nodeIdx, count) {this._primCounts[nodeIdx] = count;}

  /**
   * @param {number} nodeIdx
   * @param {number} [transparent=0]
   */
  setFlags(nodeIdx, transparent = 0) {
    this._flags[nodeIdx] = transparent << 0;
  }

  /**
   * @param {number} nodeIdx
   * @param {number} size
   */
  setLargestPrimitiveSize(nodeIdx, size) {this._largestPrimSizes[nodeIdx] = size;}

  // ----------------------------------------------------------------
  //  TreeNodes interface  (read-only after construction)
  // ----------------------------------------------------------------

  /**
   * @param {number} nodeIdx
   * @returns {number}
   */
  getChildCount(nodeIdx) {return this._childCounts[nodeIdx];}

  /**
   * @param {number} nodeIdx
   * @param {number} childIdx
   * @returns {number}
   */
  getChildIndex(nodeIdx, childIdx) {
    return this._children[this._childOffsets[nodeIdx] + childIdx];
  }

  /**
   * @param {number} nodeIdx
   * @param {THREE.Box3} dst
   */
  getBoxThree(nodeIdx, dst) {
    const off = nodeIdx * 6;
    dst.min.x = this._boxes[off];
    dst.min.y = this._boxes[off + 1];
    dst.min.z = this._boxes[off + 2];
    dst.max.x = this._boxes[off + 3];
    dst.max.y = this._boxes[off + 4];
    dst.max.z = this._boxes[off + 5];
  }

  /**
   * @param {number} nodeIdx
   * @param {Float32Array|number[]} dst
   * @param {number} [offset=0]
   */
  getBoxArray(nodeIdx, dst, offset = 0) {
    const off = nodeIdx * 6;
    dst[0 + offset] = this._boxes[off];
    dst[1 + offset] = this._boxes[off + 1];
    dst[2 + offset] = this._boxes[off + 2];
    dst[3 + offset] = this._boxes[off + 3];
    dst[4 + offset] = this._boxes[off + 4];
    dst[5 + offset] = this._boxes[off + 5];
  }

  /**
   * @param {number} nodeIdx
   * @returns {number}
   */
  getPrimStart(nodeIdx) {return this._primStarts[nodeIdx];}

  /**
   * @param {number} nodeIdx
   * @returns {number}
   */
  getPrimCount(nodeIdx) {return this._primCounts[nodeIdx];}

  /**
   * @param {number} nodeIdx
   * @returns {boolean}
   */
  isNodeTransparent(nodeIdx) {return (this._flags[nodeIdx] & 1 << 0) !== 0;}

  /**
   * @param {number} nodeIdx
   * @returns {number}
   */
  getLargestPrimitiveSize(nodeIdx) {return this._largestPrimSizes[nodeIdx];}

}

/**
 * N-ary Bounding Volume Hierarchy. Nodes may have an arbitrary number of children.
 * Implements the {@link SpatialTree} interface (see SpatialTreeInterface.js).
 */
export class NaryBVH {
  /** @type {NaryBvhNodeArray} */nodes;
  /** @type {Int32Array} */primitives;
  /** @type {object} */options;
  /** @type {Uint8Array|Uint16Array|Uint32Array} */fragId2nodeId;
  /** @type {Int32Array} */nodeParents;
  /** @type {boolean} */isBinaryTree = false;

  /**
   * @param {NaryBvhNodeArray} nodes
   * @param {Int32Array} primitives  Maps primitive slot → fragment id.
   *   The length equals the total number of fragments referenced by `nodes`
   *   (i.e. the sum of all `primCount`s), and whose values are fragment ids
   *   in `[0, primitives.length)`.
   * @param {object} options
   * @param {Uint8Array|Uint16Array|Uint32Array} [fragId2nodeId] If provided, used as-is
   *   (must match `nodes`/`primitives`); otherwise computed.
   * @param {Int32Array} [nodeParents] If provided, used as-is (must match `nodes`); otherwise computed.
   */
  constructor(nodes, primitives, options, fragId2nodeId, nodeParents) {
    this.nodes = nodes;
    this.primitives = primitives;
    this.options = options;

    // Reuse precomputed tables when given, otherwise compute them for fast look-ups.
    this.nodeParents = nodeParents ?? NaryBVH.computeNodeParents(nodes);
    this.fragId2nodeId = fragId2nodeId ?? NaryBVH.computeFragId2NodeId(nodes, primitives);
  }

  /**
   * @param {number} nodeId
   * @returns {number} parent index, or -1 for root nodes
   */
  getNodeParent(nodeId) {
    return this.nodeParents[nodeId];
  }

  /**
   *  Creates a shallow clone with its own copy of the primitives array so each clone can be sorted independently.
   * @returns {NaryBVH}
   */
  createSortableReference() {
    const primCopy = new Int32Array(this.primitives);
    // The clone shares the same node structure, so fragId2nodeId and nodeParents are
    // identical — reuse them instead of recomputing (sorting only reorders primitives
    // within each node, which doesn't change fragment→node membership or parent links).
    return new NaryBVH(this.nodes, primCopy, this.options, this.fragId2nodeId, this.nodeParents);
  }

  /**
   * Build the node-id → parent-node-id look-up table.
   * @param {NaryBvhNodeArray} nodes
   * @returns {Int32Array}
   */
  static computeNodeParents(nodes) {
    const nodeParents = new Int32Array(nodes.nodeCount);
    nodeParents.fill(-1);
    for (let parentIdx = 0; parentIdx < nodes.nodeCount; parentIdx++) {
      const childCount = nodes.getChildCount(parentIdx);
      for (let childIdx = 0; childIdx < childCount; childIdx++) {
        nodeParents[nodes.getChildIndex(parentIdx, childIdx)] = parentIdx;
      }
    }
    return nodeParents;
  }

  /**
   * Build the fragment-id → node-id look-up table.
   * @param {NaryBvhNodeArray} nodes
   * @param {Int32Array} primitives
   * @returns {Uint8Array|Uint16Array|Uint32Array}
   */
  static computeFragId2NodeId(nodes, primitives) {
    const lastNode = nodes.nodeCount - 1;

    // Note: allocateUintArray picks the narrowest unsigned typed array that can hold node indices.
    const fragId2nodeId = allocateUintArray(primitives.length, lastNode);
    for (let i = 0; i < nodes.nodeCount; i++) {
      const start = nodes.getPrimStart(i);
      const end = start + nodes.getPrimCount(i);

      for (let j = start; j < end; j++) {
        fragId2nodeId[primitives[j]] = i;
      }
    }

    return fragId2nodeId;
  }
}